import styled from '@emotion/styled'
import { AutocompleteProps, CircularProgress, InputBase, Autocomplete as MuiAutocomplete } from '@mui/material'
import { debounce } from '@mui/material/utils'
import { Stack } from '@mui/system'
import React, { useEffect } from 'react'
import { Search as SearchIcon } from 'react-feather'
import useAuth from '../hooks/useAuth'
import { formatDateYYYYMMDD } from '../packages/utils/numberFormatters'
import { normalizedName } from './entities/CompanyName'
import {
    ABBLabel,
    FELabel,
    GELabel,
    IPOLabel,
    NonCashIssueLabel,
    RELabel,
    HasWarrantLabel,
    SPECLabel,
    SPACLabel,
    RTOLabel,
    KVLabel,
} from './entities/IssueTypeLabels'
import SearchApi from '../api/SearchApi'
import { IssueType, SearchEntity } from '@finance-lab/utils/types'
import { SearchHistory } from '../contexts/SearchHistoryContext'
import { getSearchEntityLabel } from '../utils/searchHistoryMappers'

const SearchIconWrapper = styled.div`
    width: 50px;
    height: 100%;
    position: absolute;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 22px;
        height: 22px;
    }
`

const Input = styled(InputBase)`
    color: inherit;
    background-color: #f1f1f1;
    width: 100%;

    > input {
        color: ${(props) => props.theme.header.search.color};
        padding-top: ${(props) => props.theme.spacing(2.5)};
        padding-right: ${(props) => props.theme.spacing(2.5)};
        padding-bottom: ${(props) => props.theme.spacing(2.5)};
        padding-left: ${(props) => props.theme.spacing(12)};
        width: 160px;
    }
`

const dateBasedOnType = (option: Extract<SearchOption, { entityType: 'Issue' }>) => {
    if (option.issueType === 'Rights issue') {
        return option.subEndDate
    } else {
        return option.publicationDate
    }
}

export const issueTags = (
    option: Pick<Extract<SearchOption, { issueType: IssueType }>, 'issueType' | 'sharesInUnit' | 'unitExtraContent'> & {
        hasWarrants: boolean
        spac: number | null
        rto: number | null
    },
) => {
    const { spac, rto, issueType, hasWarrants, unitExtraContent, sharesInUnit } = option
    const tags = []

    const parsedSharesInUnit = sharesInUnit === null ? null : sharesInUnit
    const isSpecialUnitIssue = unitExtraContent || parsedSharesInUnit === 0

    if (issueType) {
        // TODO replace these with icon tags
        if (issueType === 'Rights issue') {
            tags.push(<FELabel key='FE' />)
        } else if (issueType === 'Directed issue') {
            tags.push(<RELabel key='RE' />)
        } else if (issueType === 'ABB Directed issue') {
            tags.push(<ABBLabel key='ABB' />)
        } else if (issueType === 'Non-cash issue') {
            if (rto) {
                tags.push(<RTOLabel key='RTO' />)
            } else {
                tags.push(<NonCashIssueLabel key='nonCash' />)
            }
        } else if (issueType === 'IPO') {
            if (spac) {
                tags.push(<SPACLabel key='SPAC' />)
            } else {
                tags.push(<IPOLabel key='IPO' />)
            }
        } else if (issueType === 'Guarantor fee issue') {
            tags.push(<GELabel key='GE' />)
        }

        if (isSpecialUnitIssue) {
            tags.push(<SPECLabel key='SPEC' />)
        }

        if (tags.length === 0) {
            tags.push(<>[no tag]</>)
        }
    }
    if (hasWarrants && !isSpecialUnitIssue) {
        tags.push(<HasWarrantLabel key='TO' />)
    }
    tags.forEach((tag, index) => {
        tags[index] = <React.Fragment key={index}>{tag}</React.Fragment>
    })
    return tags
}

/** Inner row for a live search hit or a stored history `SearchEntity` (same UI). */
function searchEntityOptionContent(entity: SearchEntity) {
    if (entity.entityType === 'Issue') {
        const numberOfWarrants =
            entity.numWarrants === null || entity.numWarrants === undefined ? null : entity.numWarrants
        return (
            <div tabIndex={0} style={{ marginLeft: '0px' }}>
                <Stack
                    direction='row'
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                    }}
                >
                    <span>{entity.instrumentName}</span>{' '}
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                        }}
                    >
                        {issueTags({ ...entity, hasWarrants: !!numberOfWarrants, spac: null, rto: null })}
                    </Stack>{' '}
                    <span>{formatDateYYYYMMDD(dateBasedOnType(entity))}</span>
                </Stack>
            </div>
        )
    }

    if (entity.entityType === 'Warrant') {
        return (
            <div tabIndex={0} style={{ marginLeft: '0px' }}>
                <Stack
                    direction='row'
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                    }}
                >
                    <span>{entity.name}</span> <HasWarrantLabel key='TO' />
                    <span>{formatDateYYYYMMDD(entity.subEndDate)}</span>
                </Stack>
            </div>
        )
    }

    return (
        <div tabIndex={0} style={{ marginLeft: '0px' }}>
            {entity.name}
        </div>
    )
}

const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: SearchOption) => {
    if (option.entityType === 'SearchHistory') {
        return (
            <li {...props} key={option.id} style={{ marginLeft: '0px' }}>
                <Stack
                    direction='row'
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        display: 'flex',
                        width: '100%',
                    }}
                >
                    <span style={{ flex: 1, minWidth: 0 }}>{searchEntityOptionContent(option.record.item)}</span>
                    <span style={{ opacity: 0.7, fontSize: '0.85em', flexShrink: 0 }}>{option.record.query}</span>
                </Stack>
            </li>
        )
    }

    if (option.entityType === 'Issue') {
        return (
            <li {...props} key={option.id}>
                {searchEntityOptionContent(option)}
            </li>
        )
    }

    if (option.entityType === 'Warrant') {
        return (
            <li {...props} key={option.id}>
                {searchEntityOptionContent(option)}
            </li>
        )
    }

    if (option.entityType === 'no-match') {
        return (
            <li {...props} style={{ marginLeft: '0px' }} key={option.id}>
                <div tabIndex={0}>{option.name}</div>
            </li>
        )
    }

    if (option.entityType === 'Convertible') {
        return (
            <li {...props} key={option.id}>
                <div tabIndex={0} style={{ marginLeft: '0px' }}>
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                        }}
                    >
                        <span>{option.name}</span> <KVLabel />
                    </Stack>
                </div>
            </li>
        )
    }

    return (
        <li {...props} style={{ marginLeft: '0px' }} key={option.id}>
            {searchEntityOptionContent(option)}
        </li>
    )
}

const typeI18n: Record<SearchOption['entityType'], string> = {
    Issue: 'Emissioner',
    Warrant: 'Optioner',
    Company: 'Bolag',
    Investor: 'Investerare',
    Convertible: 'Konvertibler',
    'no-match': 'Inga träffar',
    SearchHistory: 'Senaste sökningar',
}

const searchApi = new SearchApi()

export type SearchOption =
    | SearchEntity
    | {
          id: string
          name: string
          entityType: 'no-match'
      }
    | {
          entityType: 'SearchHistory'
          id: string
          name: string
          record: SearchHistory
      }

function searchHistoryToOption(record: SearchHistory): Extract<SearchOption, { entityType: 'SearchHistory' }> {
    const item = record.item
    return {
        entityType: 'SearchHistory',
        id: `search-history-${item.entityType}-${item.id}`,
        name: getSearchEntityLabel(item),
        record,
    }
}

function Autocomplete({
    onChange,
    placeholder,
    searchOnlyFor,
    recentHistory,
    onSearchCommit,
    onHistorySelect,
    ...props
}: Omit<AutocompleteProps<SearchOption, false, false, true>, 'onChange' | 'renderInput' | 'options'> & {
    onChange: (option: SearchEntity | null) => void
    placeholder: string
    searchOnlyFor?: SearchEntity['entityType']
    recentHistory?: SearchHistory[]
    onSearchCommit?: (entity: SearchEntity, query: string) => void
    onHistorySelect?: (record: SearchHistory) => void
}) {
    const { isAuthenticated } = useAuth()
    const [internalInputValue, setInternalInputValue] = React.useState('')
    const [options, setOptions] = React.useState<SearchOption[]>([])
    const [isLoading, setIsLoading] = React.useState(false)
    const inputValue = props.inputValue || internalInputValue

    const fetch = React.useMemo(
        () =>
            debounce(async (request: { input: string }, callback: (result: SearchEntity[]) => void) => {
                const issues = await searchApi.freeSearch(request.input)
                callback(issues)
            }, 400),
        [],
    )

    useEffect(() => {
        if (!isAuthenticated) return

        let active = true

        if (inputValue === '') {
            if (recentHistory?.length) {
                setOptions(recentHistory.map(searchHistoryToOption))
            } else {
                setOptions([])
            }
            setIsLoading(false)
            return undefined
        }

        setIsLoading(true)

        void fetch({ input: inputValue }, (results) => {
            if (active) {
                const newOptions: SearchOption[] = []

                for (let i = 0; i < results.length; i++) {
                    const result = results[i]
                    switch (result.entityType) {
                        case 'Company':
                            newOptions.push({
                                ...result,
                                name: normalizedName(result.name),
                            })
                            break
                        default:
                            newOptions.push(results[i])
                    }
                }

                // Sort by the custom order for types, then by name
                const groupOrder: Record<SearchOption['entityType'], number> = {
                    SearchHistory: 0,
                    Company: 1,
                    Issue: 2,
                    Warrant: 3,
                    Investor: 4,
                    Convertible: 5,
                    'no-match': 99,
                }
                newOptions.sort((a, b) => {
                    const groupComparison = (groupOrder[a.entityType] || 99) - (groupOrder[b.entityType] || 99)
                    if (groupComparison !== 0) return groupComparison
                    if (a.entityType === 'Warrant' && b.entityType === 'Warrant') return 0
                    if (a.entityType === 'Issue' || b.entityType === 'Issue') return 0

                    return a.name?.localeCompare(b.name ?? '') || 0
                })

                setOptions(newOptions)
                setIsLoading(false)
            }
        })

        return () => {
            active = false
        }
    }, [inputValue, fetch, isAuthenticated, recentHistory])

    const handleChange = (event: unknown, searchOption: SearchOption | string | null) => {
        if (searchOption === null) {
            onChange(null)
            return
        }

        if (typeof searchOption === 'string') {
            console.warn('Unexpected searchOption of type `string`.')
            return
        }

        if (searchOption.entityType === 'SearchHistory') {
            onChange(searchOption.record.item)
            onHistorySelect?.(searchOption.record)
            setInternalInputValue('')
            return
        }

        onChange(searchOption as SearchEntity)
        onSearchCommit?.(searchOption as SearchEntity, inputValue)
        setInternalInputValue('')
    }

    // https://mui.com/material-ui/react-autocomplete/#google-maps-place

    // Kolla här för gruppering av resultaten: https://refine.dev/blog/material-ui-autocomplete-component/#groupby

    return (
        <MuiAutocomplete<SearchOption, false, false, true>
            clearOnEscape
            id='search'
            inputValue={inputValue}
            onChange={handleChange}
            onInputChange={(event, newInputValue) => {
                setInternalInputValue(newInputValue)
            }}
            freeSolo
            fullWidth
            getOptionDisabled={(o) => o.id === 'no-match'}
            options={options}
            filterOptions={(options) => {
                const filteredOptions = searchOnlyFor
                    ? options.filter(({ entityType }) => entityType === searchOnlyFor)
                    : options

                if (filteredOptions.length > 0) return filteredOptions
                if (!inputValue) return filteredOptions
                if (isLoading) return filteredOptions

                return [
                    {
                        id: 'no-match',
                        entityType: 'no-match',
                        name: 'Inga träffar',
                    },
                ]
            }}
            renderInput={(params) => (
                <Input
                    ref={params.slotProps.input.ref}
                    inputProps={params.slotProps.htmlInput}
                    placeholder={placeholder}
                    startAdornment={
                        <SearchIconWrapper>
                            <SearchIcon />
                        </SearchIconWrapper>
                    }
                    endAdornment={isLoading ? <CircularProgress size={15} sx={{ marginRight: 4 }} /> : null}
                />
            )}
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : 'name' in option && option.name ? option.name : ''
            }
            renderOption={renderOption}
            groupBy={(option) => typeI18n[option.entityType] || option.entityType}
            noOptionsText='Inga träffar'
            renderGroup={(params) =>
                searchOnlyFor ? (
                    params.children
                ) : (
                    <li key={params.key} style={{ marginBottom: '8px' }}>
                        <span style={{ marginLeft: '16px', marginBottom: '4px', display: 'flex', fontWeight: 'bold' }}>
                            {params.group}
                        </span>
                        <ul style={{ paddingLeft: '16px' }}>{params.children}</ul>
                    </li>
                )
            }
            {...props}
        />
    )
}

export default Autocomplete
